/**
 * Context-locked colour palette for the Evacuation Orders & Alerts layer (#204,
 * operator-cop-doctrine §3A.7 / §8.4). The "protective-action" register — distinct
 * from the hazard-SOURCE families (fire red, flood/CAP blue, tornado cerise): an
 * evacuation polygon answers "who must move," not "what the hazard is," so it owns
 * its own VIOLET family rather than borrowing a hazard hue.
 *
 * Two register levels, colour ALWAYS paired with the register label at the call
 * site (never colour alone):
 *   • ORDER  — mandatory leave NOW. The heavier register: deeper violet, bolder
 *              outline (matches the Extreme-CAP "width, not motion" convention).
 *   • ALERT  — be ready to leave. The lighter register: a paler violet.
 *
 * Colour Law (design-council Part IX): strokes clamp to ≤0.9 and NO hue
 * impersonates the urgent-anomaly red `#ff3b30` (Law 5) — violet is unmistakably
 * not that red. Never pulses (§3B.8 motion budget: only live-cam + L3/L4 anomalies).
 *
 * Fill register — founder-authorized BRIGHTER ceiling (#214-followup, 2026-06-08).
 * An evacuation polygon is a DECISION-DRIVING protective-action register, not the
 * ambient hazard-context fill that the generic ≤0.18 area ceiling governs — the
 * founder asked for a fill an operator can skim at a glance. The evac family
 * therefore carries its own raised ambient ceiling (≤0.30): still clearly
 * translucent (the map reads through it), still violet, still no red collision —
 * just less transparent than a background hazard area. ORDER stays heavier than
 * ALERT (deeper fill + wider outline, not motion).
 *
 * This module must NOT import another palette family (ESLint no-restricted-imports).
 */
import type { EvacSeverity } from '@tsr/shared-types';

export interface EvacuationStyle {
  /** CSS hex stroke/fill colour. */
  color: string;
  /** Fill opacity 0–1 (protective-action register, ≤0.30 — brighter than the
   *  generic ≤0.18 ambient-hazard ceiling so an operator can skim it). */
  alpha: number;
  /** Stroke (outline) opacity — bolder than the fill for legibility. */
  strokeAlpha: number;
  /** Outline width (px) — ORDER reads heavier than ALERT via width, not motion. */
  outlineWidth: number;
  /** Operator-facing register label (always paired with the colour). */
  label: string;
}

const ORDER: EvacuationStyle = {
  color: '#6d28d9', // violet-700 — mandatory-leave, the heavier register
  alpha: 0.3, // brighter protective-action fill (was 0.18) — skimmable at a glance
  strokeAlpha: 0.9,
  outlineWidth: 4,
  label: 'Evacuation Order',
};

const ALERT: EvacuationStyle = {
  color: '#a78bfa', // violet-400 — be-ready, the lighter register
  alpha: 0.22, // brighter (was 0.14), stays below ORDER so the registers read apart
  strokeAlpha: 0.85,
  outlineWidth: 2,
  label: 'Evacuation Alert',
};

/** Fallback for a feature with an unknown/missing severity — neutral grey, never
 *  dropped (an unrecognised evacuation feature still renders). */
const UNKNOWN: EvacuationStyle = {
  color: '#9ca3af',
  alpha: 0.18, // brighter (was 0.12) in step with the family, still under ORDER
  strokeAlpha: 0.7,
  outlineWidth: 2,
  label: 'Evacuation area',
};

/** Resolve the style for an evacuation severity register (null/other → grey). */
export function evacuationStyle(severity: string | null | undefined): EvacuationStyle {
  const s = (severity ?? '').toLowerCase() as EvacSeverity | '';
  if (s === 'order') return ORDER;
  if (s === 'alert') return ALERT;
  return UNKNOWN;
}
